import { useApiClient } from "@/hooks/useApiClient";
import useSWR from "swr";
import useSWRMutation, { type SWRMutationResponse } from "swr/mutation";
import { type AxiosError, isAxiosError } from "axios";

export interface CompanyInfoAPIResponse {
  companyOverview: string;
  companyLogoUri: string;
}

export interface CompanyInfoUpdateAPIResponse extends CompanyInfoAPIResponse {}

const url = "/user/config/company";

/**
 * 会社情報の取得API
 * @returns 成功ならUserConfigAPIResponse、失敗ならnull
 */
export const CompanyInfoGetAPI = (): CompanyInfoAPIResponse | null => {
  const { apiClient } = useApiClient();

  const fetcher = async (
    url: string
  ): Promise<CompanyInfoAPIResponse | null> => {
    const res = await apiClient
      .get<CompanyInfoAPIResponse>(url)
      .catch((err: AxiosError) => {
        if (isAxiosError(err) && err.response && err.response.status === 404) {
          return null;
        }
        throw err;
      });
    return res?.data ?? null;
  };

  const { data } = useSWR<CompanyInfoAPIResponse | null, number | undefined>(
    url,
    fetcher,
    {
      revalidateOnFocus: false,
    }
  );
  return data ?? null;
};

/**
 * 会社情報の更新API
 * @returns trigger()が返ってくるのでtrigger(data)で設定を送信することができます
 */
export const CompanyInfoUpdateAPI = (): [
  SWRMutationResponse<
    CompanyInfoUpdateAPIResponse,
    never,
    "/user/config/company/overview",
    { companyOverview: string }
  >,
  SWRMutationResponse<
    CompanyInfoUpdateAPIResponse,
    never,
    "/user/config/company/logo",
    FormData
  >
] => {
  const urlOverview = "/user/config/company/overview";
  const urlLogo = "/user/config/company/logo";

  const { apiClient } = useApiClient();

  const overviewFetcher = async (
    url: string,
    { arg }: { arg: { companyOverview: string } }
  ): Promise<CompanyInfoUpdateAPIResponse> => {
    return (await apiClient.put<CompanyInfoUpdateAPIResponse>(url, arg)).data;
  };

  const logoFetcher = async (
    url: string,
    { arg }: { arg: FormData }
  ): Promise<CompanyInfoUpdateAPIResponse> => {
    return (await apiClient.put<CompanyInfoUpdateAPIResponse>(url, arg)).data;
  };

  const overview = useSWRMutation<
    CompanyInfoUpdateAPIResponse,
    never,
    "/user/config/company/overview",
    { companyOverview: string }
  >(urlOverview, overviewFetcher, { revalidate: false });
  const logo = useSWRMutation<
    CompanyInfoUpdateAPIResponse,
    never,
    "/user/config/company/logo",
    FormData
  >(urlLogo, logoFetcher, { revalidate: false });

  return [overview, logo];
};
